<script setup lang="ts">

import {computed, inject, ref, watch} from "vue";
import {useStore} from "vuex";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import ItemV3StonesParameterLoading from "@/components/itemV3/parameter/stones/parameter/itemV3StonesParameterLoading.vue";

const props = defineProps<{ groupIdx: number }>();
const emit = defineEmits(["patch"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

/**
 * session
 *
 * Transferred from itemV3StonesSetting.vue: configureAll/itemIdx are wired into
 * ParametersVariations the same way, the variant is fetched per stone group via
 * getItemVariants(itemIdx, "stones.<groupIdx>.stone_waves") instead of a fixed idx.
 *
 * Note: no "stone_waves" idx is confirmed in the locales yet (unlike stone_form/
 * stone_setting/stone_size); the idx name follows the existing "stone_*" naming
 * convention and may need to be adjusted once the real backend attribute is known.
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = ""

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

/**
 * stone waves - stones.<groupIdx>.stone_waves
 */

const stoneWaves = ref({
  "stone_waves": {},
  "stone_line_angle": {},
  "amplitude": {}
} as any)

const getStoneWavesData = (groupIdy: number) => {
  if (groupIdy >= 0) {
    stoneWaves.value.stone_waves = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".waves")
    stoneWaves.value.stone_line_angle = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".stone_line_angle")

    // amplitude is only relevant once a waves value other than idx 0 ("gerade" - no waves) is matched
    const matchedWave = (stoneWaves.value.stone_waves[0]?.values || []).find((v: any) => v.match)
    stoneWaves.value.amplitude = (matchedWave && matchedWave.idx.toString() !== "0")
        ? parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".amplitude")
        : {}
  }
}

watch(() => props.groupIdx, (groupIdy) => {
  getStoneWavesData(groupIdy)
}, {immediate: true})

watch(session, () => {
  getStoneWavesData(props.groupIdx)
}, {deep: true})

/**
 * waves variants (transferred from itemV3StonesSetting.vue's "setting variants")
 */

const active = computed(() => stoneWaves.value.stone_waves[0])
const activeLineAngle = computed(() => stoneWaves.value.stone_line_angle[0])

const variantsList = computed(() => active.value?.values || [])
const lineAngleVariantsList = computed(() => activeLineAngle.value?.values || [])

/**
 * combined list - waves values followed by stone_line_angle values (appended at the end).
 * the stone_line_angle value with idx 0 is always ignored, the rest stays.
 */

const combinedVariantsList = computed(() => {
  // if a stone_line_angle value is matched, the waves match must not be shown as active
  const lineAngleMatched = lineAngleVariantsList.value.some((v: any) => v.match && v.idx.toString() !== "0")

  const waves = variantsList.value.map((v: any) => ({
    ...v,
    match: lineAngleMatched ? false : v.match,
    _actionIdx: active.value?.idx,
    _source: 'waves'
  }))

  const lineAngleValues = lineAngleVariantsList.value.filter((v: any) => v.idx.toString() !== "0")

  const lineAngle = lineAngleValues.map((v: any) => ({...v, _actionIdx: activeLineAngle.value?.idx, _source: 'stone_line_angle'}))

  return [...waves, ...lineAngle]
})

/*
 * focus values (transferred from itemV3StonesSetting.vue's "focus values")
 *
 * No curated list of "stone_waves" option keys is known yet, so the focus list
 * starts empty - getFocusValueIdx will still always surface the currently
 * matched option, everything else stays behind the "more" trigger.
 */

const focusValueIdx: number[] = [
  // idx must match one of the stone_waves option keys (see locales/*.json), once known
  0, 1, 2, 3, 45, 90, 135
]

const combinedMatchedIdx = computed(() => combinedVariantsList.value.findIndex((v: any) => v.match))

const getFocusValueIdx = computed(() => {
  if (combinedMatchedIdx.value !== -1 && !focusValueIdx.includes(combinedVariantsList.value[combinedMatchedIdx.value].idx)) {
    return [
      combinedVariantsList.value[combinedMatchedIdx.value].idx,
      ...focusValueIdx.slice(0, -1)
    ]
  }

  return focusValueIdx
})

const hasMoreValues = computed(() => combinedVariantsList.value.some((v: any) => !getFocusValueIdx.value.includes(v.idx)))

// whether the "more" values (list entry with the right-arrow) have been revealed inline
const showMore = ref(false)

/**
 * waves title - 0 = "gerade", 1 = "saturn", 2 and more = "<idx> Wellen"
 */

const getWavesTitle = (idx: string) => {
  const wavesCount = parseInt(idx, 10)

  if (wavesCount === 0) {
    return "gerade"
  }

  if (wavesCount === 1) {
    return "saturn"
  }

  return `${wavesCount} Wellen`
}

/**
 * variant title - stone_line_angle idx 90 gets its own text ("quer"), everything else
 * (waves values and all other stone_line_angle values) falls back to getWavesTitle
 */

const getVariantTitle = (variantData: any) => {
  if (variantData._source === 'stone_line_angle' && variantData.idx.toString() === "90") {
    return "quer"
  } else if (variantData._source === 'stone_line_angle' && (variantData.idx.toString() === "45" || variantData.idx.toString() === "135")) {
    return variantData.idx + "°"
  }

  return getWavesTitle(variantData.idx)
}

/**
 * Patch
 */

const handleValuePatch = (value: any) => {
  emit('patch', {
    action: value._actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * amplitude - stones.<groupIdx>.amplitude - step slider (transferred from
 * itemV3StonesStoneSize.vue's "stone size - step slider"), only loaded/shown once a
 * waves value other than idx 0 ("gerade" - no waves) is matched, see getStoneWavesData
 */

const amplitudeValues = computed(() => stoneWaves.value.amplitude[0]?.values || [])

const amplitudeIdx = computed(() => {
  const idx = amplitudeValues.value.findIndex((v: any) => v.match)
  return idx === -1 ? 0 : idx
})

// local slider position, so dragging the handle only updates the UI - the api
// call is only sent once the user drops (releases) the slider, see onAmplitudeRangeChange
const sliderAmplitudeIdx = ref(0)

watch(amplitudeIdx, (idx) => {
  sliderAmplitudeIdx.value = idx
}, {immediate: true})

const amplitudeSliderPercent = computed(() => {
  if (amplitudeValues.value.length < 2) return 0
  return (sliderAmplitudeIdx.value / (amplitudeValues.value.length - 1)) * 100
})

const currentAmplitude = computed(() => amplitudeValues.value[sliderAmplitudeIdx.value])

const stepAmplitude = (delta: number) => {
  if (!stoneWaves.value.amplitude[0]) return
  const newIdx = amplitudeIdx.value + delta
  if (newIdx < 0 || newIdx >= amplitudeValues.value.length) return
  handleValuePatch({...amplitudeValues.value[newIdx], _actionIdx: stoneWaves.value.amplitude[0].idx})
}

const onAmplitudeRangeInput = (event: Event) => {
  sliderAmplitudeIdx.value = parseInt((event.target as HTMLInputElement).value)
}

const onAmplitudeRangeChange = (event: Event) => {
  if (!stoneWaves.value.amplitude[0]) return
  const idx = parseInt((event.target as HTMLInputElement).value)
  const value = amplitudeValues.value[idx]
  if (value) handleValuePatch({...value, _actionIdx: stoneWaves.value.amplitude[0].idx})
}

const getImageIdx = (idx: number) => {
  if (idx == 0) {
    return "straight"
  } else if (idx == 90){
    return "cross"}
  else if (idx == 45 || idx == 135){
      return idx
  } else {
    return idx + "sin"
  }
}

</script>

<template>
  <div v-if="active" class="w-full">
    <ul class="flex flex-wrap mb-2 w-full justify-center ">
      <!-- stone waves list - focus values -->
      <template v-for="(variantData, variantIdx) in combinedVariantsList">
        <li :key="variantIdx"
            v-if="getFocusValueIdx.includes(variantData.idx)"
            class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
            @click="handleValuePatch(variantData)"
            :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': variantData.match,
              'border-2 border-transparent': !variantData.match
            }"
        >
          <div
              class="h-auto w-13 rounded-2xl bg-white overflow-hidden relative flex items-center justify-center">
            <img
                :src="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/waves/400x300/channel_${getImageIdx(variantData.idx)}.webp`"
                :srcset="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/waves/800x600/channel_${getImageIdx(variantData.idx)}.webp`"
                class="w-full h-full object-cover relative"
            >
          </div>
          <span class="text-parameter-text text-[10px] overflow-hidden max-w-13 mx-auto text-center mt-1">
            {{ getVariantTitle(variantData) }}
          </span>
        </li>
      </template>
      <!-- "more" trigger - placed as the last entry of the focus list, looks like another list item -->
      <li v-if="hasMoreValues && !showMore"
          class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] border-2 border-transparent"
          @click="showMore = true"
      >
        <div class="h-auto w-13 rounded-2xl flex items-center justify-center relative bg-[#f3f0e9]">
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none" class="size-2.5 shrink-0 -rotate-90 text-[#555956]">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="text-parameter-text text-[10px] overflow-hidden max-w-13 mx-auto text-center mt-1">
          Mehr
        </span>
      </li>
      <!-- stone waves list - more values (revealed inline once the "more" entry above was clicked) -->
      <template v-for="(variantData, variantIdx) in combinedVariantsList">
        <li :key="'more-' + variantIdx"
            v-if="showMore && !getFocusValueIdx.includes(variantData.idx)"
            class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
            @click="handleValuePatch(variantData)"
            :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': variantData.match,
              'border-2 border-transparent': !variantData.match
            }"
        >
          <div
              class="h-132 w-13 rounded-2xl bg-white overflow-hidden relative flex items-center justify-center">
            <img
                :src="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/waves/400x300/channel_${variantData.idx}.webp`"
                :srcset="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/waves/800x600/channel_${variantData.idx}.webp`"
                class="w-full h-full object-cover relative"
            >
          </div>
          <span class="text-parameter-text text-[10px] overflow-hidden max-w-13 mx-auto text-center mt-1">
            {{ getVariantTitle(variantData) }}
          </span>
        </li>
      </template>
      <!-- "less" trigger - placed as the last entry once "more" is revealed, collapses the list again -->
      <li v-if="showMore"
          class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] border-2 border-transparent"
          @click="showMore = false"
      >
        <div class="h-14 w-14 rounded-2xl flex items-center justify-center relative bg-[#f3f0e9]">
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none" class="size-2.5 shrink-0 rotate-90 text-[#555956]">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
          Weniger
        </span>
      </li>
    </ul>
    <!-- amplitude - step slider, only shown once a waves value other than idx 0 ("gerade") is matched -->
    <div v-if="stoneWaves.amplitude[0]">
      <div class="flex items-center gap-2">
        <button
            type="button"
            class="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#e7ddcc] text-[#8f8a80] transition-colors hover:bg-[#f3f0e9] hover:text-brand-main disabled:cursor-not-allowed disabled:opacity-30"
            :disabled="amplitudeIdx <= 0"
            @click="stepAmplitude(-1)"
        >
          −
        </button>
        <span
            class="flex-1 rounded-xl border border-[#e7ddcc] py-1.5 text-center text-xs font-medium text-parameter-title">
          {{ currentAmplitude?.title }}
        </span>
        <button
            type="button"
            class="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#e7ddcc] text-[#8f8a80] transition-colors hover:bg-[#f3f0e9] hover:text-brand-main disabled:cursor-not-allowed disabled:opacity-30"
            :disabled="amplitudeIdx >= amplitudeValues.length - 1"
            @click="stepAmplitude(1)"
        >
          +
        </button>
      </div>
      <div class="relative mt-4 flex h-4 items-center select-none">
        <!-- track -->
        <div class="pointer-events-none absolute inset-x-0 h-[3px] rounded-full bg-[#e7ddcc]">
          <div class="h-full rounded-full bg-brand-main" :style="{ width: amplitudeSliderPercent + '%' }"></div>
        </div>
        <!-- thumb -->
        <div
            class="pointer-events-none absolute top-1/2 size-4 rounded-full bg-brand-main shadow-md"
            :style="{ left: amplitudeSliderPercent + '%', transform: 'translate(-50%, -50%)' }"
        ></div>
        <!-- native range input, kept invisible but interactive -->
        <input
            type="range"
            class="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
            min="0"
            :max="amplitudeValues.length - 1"
            step="1"
            :value="sliderAmplitudeIdx"
            @input="onAmplitudeRangeInput"
            @change="onAmplitudeRangeChange"
        />
      </div>
      <div class="mt-1 flex justify-between text-[10px] text-parameter-text">
        <span>{{ amplitudeValues[0]?.title }}</span>
        <span>{{ amplitudeValues[amplitudeValues.length - 1]?.title }}</span>
      </div>
    </div>
  </div>
  <item-v3-stones-parameter-loading v-else/>
</template>

<style scoped>
</style>
